import { motion, useReducedMotion } from 'framer-motion'

type Props = { badge: string; label: string } // label = "Conforme"

/**
 * B3 — COMPLIANCE SHIELD (hero signature, AFSCA).
 * A shield outline draws on as a closed contour (motion.path pathLength 0→1, house
 * easing) around a central food picto (box + leaf). On completion a ✓ strokes in and
 * props.label fades up. The contour then STAYS drawn — a spark rides the contour on a
 * gentle perpetual loop (never blanks out) + breathing accent glow keep it alive.
 * Reduced motion → fully drawn shield + ✓ + label, fully static.
 */

const EASE = [0.22, 1, 0.36, 1] as const

// SVG geometry (140 × 140 viewBox, shield centered).
const CX = 70

// Closed shield contour — single path, drawn as one continuous stroke.
const SHIELD_PATH =
  'M70 14 L116 30 V70 C116 99 96 116 70 128 C44 116 24 99 24 70 V30 Z'

// Check mark inside the shield.
const CHECK_PATH = 'M52 70 L65 84 L92 54'

export default function AfscaShield({ badge, label }: Props) {
  const reduce = useReducedMotion()

  // Timeline (seconds) — plays ONCE on mount, then stays drawn.
  const T_DRAW = 1.4 // shield contour draw-on
  const T_CHECK_DELAY = 1.5 // ✓ starts just after contour closes
  const T_CHECK = 0.6
  const T_LABEL_DELAY = 2.1 // label fades up after ✓

  return (
    <div className="relative w-full rounded-2xl sm:rounded-3xl border border-ink-100 bg-gradient-to-br from-white via-accent-50/30 to-white overflow-hidden isolate aspect-square sm:aspect-[5/4]">
      {/* dot pattern */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.32]"
        style={{
          backgroundImage:
            'radial-gradient(rgba(59,93,255,0.12) 1px, transparent 1px)',
          backgroundSize: '22px 22px',
          maskImage:
            'radial-gradient(ellipse at center, black 30%, transparent 80%)',
        }}
      />

      {/* breathing accent halo behind the shield */}
      {reduce ? (
        <div
          aria-hidden
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full blur-3xl"
          style={{
            background:
              'radial-gradient(circle, rgba(59,93,255,0.20), transparent 70%)',
          }}
        />
      ) : (
        <motion.div
          aria-hidden
          animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.7, 0.4] }}
          transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full blur-3xl"
          style={{
            background:
              'radial-gradient(circle, rgba(59,93,255,0.22), transparent 70%)',
          }}
        />
      )}

      {/* second softer top halo */}
      {!reduce && (
        <motion.div
          aria-hidden
          animate={{ scale: [1, 1.12, 1], opacity: [0.25, 0.45, 0.25] }}
          transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -right-16 -top-12 w-56 h-56 rounded-full blur-3xl"
          style={{
            background:
              'radial-gradient(circle, rgba(16,185,129,0.16), transparent 70%)',
          }}
        />
      )}

      {/* top-left badge pill */}
      <div className="absolute left-3 top-3 sm:left-4 sm:top-4 z-10">
        <span className="inline-flex items-center gap-2 rounded-full border border-accent-200 bg-accent-50/90 backdrop-blur-sm px-2.5 py-1 text-[10px] sm:text-xs text-accent-800">
          <span className="relative flex h-1.5 w-1.5">
            {!reduce && (
              <span className="absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-60 animate-ping" />
            )}
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent-600" />
          </span>
          <span className="font-medium">{badge}</span>
        </span>
      </div>

      {/* shield + picto + check + label */}
      <div className="absolute inset-0 flex flex-col items-center justify-center px-6 pb-6">
        <div className="relative w-[58%] max-w-[260px] aspect-square">
          <svg
            aria-hidden
            viewBox="0 0 140 140"
            className="absolute inset-0 w-full h-full overflow-visible"
            preserveAspectRatio="xMidYMid meet"
          >
            <defs>
              <linearGradient id="afscaShieldStroke" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#3b5dff" />
                <stop offset="55%" stopColor="#242fd6" />
                <stop offset="100%" stopColor="#3b5dff" />
              </linearGradient>
              <linearGradient id="afscaShieldFill" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="rgba(59,93,255,0.10)" />
                <stop offset="100%" stopColor="rgba(59,93,255,0.02)" />
              </linearGradient>
            </defs>

            {/* soft shield fill — fades in with the contour, then stays */}
            <motion.path
              d={SHIELD_PATH}
              fill="url(#afscaShieldFill)"
              stroke="none"
              initial={reduce ? false : { opacity: 0 }}
              animate={reduce ? { opacity: 1 } : { opacity: [0, 0, 0.9] }}
              transition={
                reduce
                  ? undefined
                  : { duration: T_DRAW + 0.3, times: [0, 0.5, 1], ease: EASE }
              }
            />

            {/* draw-on closed contour — the signature motif (plays once, then stays) */}
            <motion.path
              d={SHIELD_PATH}
              fill="none"
              stroke="url(#afscaShieldStroke)"
              strokeWidth={4}
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={reduce ? false : { pathLength: 0, opacity: 0.85 }}
              animate={reduce ? { pathLength: 1 } : { pathLength: 1, opacity: 1 }}
              transition={reduce ? undefined : { duration: T_DRAW, ease: EASE }}
            />

            {/* spark riding the contour: leads the initial draw, then a gentle
                perpetual sweep around the (already drawn) shield — never blanks. */}
            {!reduce && (
              <motion.circle
                r={3.4}
                fill="#fff"
                stroke="#3b5dff"
                strokeWidth={1.4}
                initial={{ opacity: 0, offsetDistance: '0%' }}
                animate={{
                  opacity: [0, 1, 1, 0],
                  offsetDistance: ['0%', '100%'],
                }}
                transition={{
                  duration: T_DRAW,
                  ease: EASE,
                  times: [0, 0.08, 0.9, 1],
                  repeat: Infinity,
                  repeatDelay: 3.4,
                }}
                style={{ offsetPath: `path('${SHIELD_PATH}')`, offsetRotate: '0deg' }}
              />
            )}

            {/* central food picto: box + leaf */}
            <motion.g
              initial={reduce ? false : { opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={reduce ? undefined : { duration: 0.6, delay: 0.5, ease: EASE }}
              style={{ transformOrigin: '50% 50%', transformBox: 'fill-box' }}
            >
              {/* parcel / food box */}
              <path
                d="M54 56 L70 49 L86 56 L86 76 L70 83 L54 76 Z"
                fill="none"
                stroke="#8c8c97"
                strokeWidth={2.4}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              <path
                d="M54 56 L70 63 L86 56 M70 63 L70 83"
                fill="none"
                stroke="#8c8c97"
                strokeWidth={2.4}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              {/* leaf — food / fresh marker */}
              <path
                d="M70 47 C61 41 53 43 50 50 C56 51 64 50 70 47 Z"
                fill="#10b981"
                opacity={0.9}
              />
              <path
                d="M70 47 C64 46 57 47 51 49.5"
                fill="none"
                stroke="#0e9f72"
                strokeWidth={1.4}
                strokeLinecap="round"
              />
            </motion.g>

            {/* ✓ check strokes in after the contour closes, then stays */}
            <motion.path
              d={CHECK_PATH}
              fill="none"
              stroke="#10b981"
              strokeWidth={5}
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={reduce ? false : { pathLength: 0, opacity: 0 }}
              animate={reduce ? { pathLength: 1, opacity: 1 } : { pathLength: 1, opacity: 1 }}
              transition={
                reduce
                  ? undefined
                  : {
                      pathLength: { duration: T_CHECK, delay: T_CHECK_DELAY, ease: EASE },
                      opacity: { duration: 0.2, delay: T_CHECK_DELAY },
                    }
              }
            />
          </svg>
        </div>

        {/* label — "Conforme" fades up below the shield, then stays */}
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={reduce ? undefined : { duration: 0.6, delay: T_LABEL_DELAY, ease: EASE }}
          className="mt-4 sm:mt-5 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50/90 backdrop-blur-sm px-4 py-1.5"
        >
          <span className="flex items-center justify-center w-4 h-4 rounded-full bg-emerald-500">
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={3.5}>
              <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <span className="text-sm sm:text-base font-semibold tracking-tight text-emerald-700">
            {label}
          </span>
        </motion.div>
      </div>
    </div>
  )
}
